import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as UploadIcon } from 'assets/icons/cloud.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import Button from 'components/Button/Button';
import ImportErrorsModal from 'components/ImportErrorsModal/ImportErrorsModal';
import MappingModal from 'components/MappingModal/MappingModal';
import Modal from 'components/Modal/Modal';
import QuestionErrorModal from 'components/QuestionErrorModal/QuestionErrorModal';
import { DATA_SETS_MIME_TYPES_ACCEPT, MAX_LOGO_SIZE } from 'constants/general';
import { BUTTON_VARIANT, PRESIGNED_FILE_TYPES } from 'constants/interfaces';
import useFileStorageUpload from 'hooks/useFileStorageUpload';
import useImportErrorModal from 'hooks/useImportErrorModal';
import { useAppDispatch } from 'store/hooks/useApp';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import {
  useImportAgentSessionSupplierDiscoveryXlsxMutation,
  useLazyGetAgentSessionSupplierDiscoveryXlsxExampleQuery,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type { TImportRecordsMutation } from 'components/MappingModal/interfaces/TImportRecordsMutation';
import type { TPresignedFileTypes } from 'constants/interfaces';
import type IProfitCenterImportRecordsRequest from 'store/slices/dataSets/interfaces/IProfitCenterImportRecordsRequest';
import type IImportAgentSessionSupplierDiscoveryXlsxSheet
  from 'store/slices/valueChainMap/interfaces/IImportAgentSessionSupplierDiscoveryXlsxSheet';
import type IAICreatorSupplierUploadModal from './interfaces/IAICreatorSupplierUploadModal';

import styles from '../AICreatorBoMUploadModal/scss/AICreatorBoMUploadModal.module.scss';

function AICreatorSupplierUploadModal({
  open,
  valueChainMapId,
  sessionId,
  unitId,
  onClose,
  onImported,
}: IAICreatorSupplierUploadModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [dataFile, setDataFile] = useState<File | null>(null);
  const [dropzoneError, setDropzoneError] = useState('');
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false);
  const [isImportErrorsModalVisible, setIsImportErrorsModalVisible] = useState(false);
  const { uploadToStorage, isLoading: isPresignedPostLoading } = useFileStorageUpload();
  const [downloadExample, { isFetching: isDownloadExampleLoading }] = (
    useLazyGetAgentSessionSupplierDiscoveryXlsxExampleQuery()
  );
  const [importAgentSessionSupplierDiscoveryXlsx, { isLoading: isImportLoading }] = (
    useImportAgentSessionSupplierDiscoveryXlsxMutation()
  );
  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const isBusy = isPresignedPostLoading || isImportLoading;
  const isUploadDisabled = isBusy || !valueChainMapId || sessionId == null || unitId == null;

  const resetUploadState = useCallback(() => {
    setDataFile(null);
    setDropzoneError('');
  }, []);

  const handleCloseMappingModal = useCallback(() => {
    setIsMappingModalVisible(false);
  }, []);

  const handleCloseImportErrorsModal = useCallback((value: boolean) => {
    setIsImportErrorsModalVisible(value);
  }, []);

  const handleDownloadExample = useCallback(() => {
    if (!valueChainMapId || sessionId == null) {
      return;
    }

    downloadExample({
      valueChainMapId,
      sessionId,
    });
  }, [downloadExample, sessionId, valueChainMapId]);

  const uploadFile = useCallback((file: File | string, fileType: TPresignedFileTypes) => {
    if (typeof file === 'object') {
      return uploadToStorage(file, fileType)
        .then((presignedData) => presignedData.fields.Url);
    }

    return Promise.resolve(file);
  }, [uploadToStorage]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }

    resetUploadState();
    setIsMappingModalVisible(false);
    setIsImportErrorsModalVisible(false);
    handleCloseImportErrorModal();
    onClose();
  }, [handleCloseImportErrorModal, isBusy, onClose, resetUploadState]);

  const handleImportSuccess = useCallback(() => {
    toast.success(t('notifications.valueChainSupplierUploadSuccess'));
    onImported?.();
    onClose();
  }, [onClose, onImported, t]);

  useEffect(() => {
    if (!open) {
      resetUploadState();
      setIsMappingModalVisible(false);
      setIsImportErrorsModalVisible(false);
      handleCloseImportErrorModal();
    }
  }, [handleCloseImportErrorModal, open, resetUploadState]);

  const handleImportFile = useCallback((file: File) => {
    if (!valueChainMapId || sessionId == null || unitId == null) {
      return;
    }

    setDropzoneError('');
    setDataFile(file);

    uploadFile(file, PRESIGNED_FILE_TYPES.valueChainAttachment)
      .then((url) => importAgentSessionSupplierDiscoveryXlsx({
        valueChainMapId,
        sessionId,
        unitId,
        url,
        dryRun: false,
        Main: {
          source: '',
          map: [],
        },
      }).unwrap())
      .then((response) => {
        dispatch(setDataSetsState({ mappingColumns: response.data }));
        setIsMappingModalVisible(true);
        resetUploadState();
      })
      .catch((error: unknown) => {
        handleImportError(error);
        resetUploadState();
      });
  }, [
    dispatch,
    handleImportError,
    importAgentSessionSupplierDiscoveryXlsx,
    resetUploadState,
    sessionId,
    unitId,
    uploadFile,
    valueChainMapId,
  ]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const [file] = acceptedFiles;

    if (!file) {
      return;
    }

    handleImportFile(file);
  }, [handleImportFile]);

  const onDropRejected = useCallback((fileRejections: { errors: { message: string }[] }[]) => {
    setDataFile(null);
    setDropzoneError(fileRejections[0]?.errors.map((item) => item.message).join(', ') || '');
  }, []);

  const handleSupplierImportRecords = useCallback<TImportRecordsMutation>((
    request: IProfitCenterImportRecordsRequest,
  ) => {
    if (!valueChainMapId || sessionId == null || unitId == null) {
      return {
        unwrap: () => Promise.reject(new Error(t('notifications.somethingWentWrong'))),
      };
    }

    const main: IImportAgentSessionSupplierDiscoveryXlsxSheet | undefined = request.Main
      ? {
        source: request.Main.source,
        map: request.Main.map?.map((item) => ({
          target: item.target,
          source: item.source || '',
        })),
      }
      : undefined;

    return importAgentSessionSupplierDiscoveryXlsx({
      valueChainMapId,
      sessionId,
      unitId,
      url: request.url,
      dryRun: request.dryRun,
      ...(main ? { Main: main } : {}),
    });
  }, [
    importAgentSessionSupplierDiscoveryXlsx,
    sessionId,
    t,
    unitId,
    valueChainMapId,
  ]);

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    accept: DATA_SETS_MIME_TYPES_ACCEPT,
    maxSize: MAX_LOGO_SIZE,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isUploadDisabled,
    onDrop,
    onDropRejected,
  });

  const rootProps = getRootProps();

  const dropZoneClassName = useMemo(() => [
    styles.dropZone,
    isDragActive ? styles.dropZoneDragActive : '',
    dropzoneError ? styles.dropZoneError : '',
  ].filter(Boolean).join(' '), [dropzoneError, isDragActive]);

  return (
    <>
      <Modal
        open={open}
        title={t('valueChainMap.bomUnitsUploadModalTitle')}
        className={styles.modal}
        titleClassName={styles.titleWrapper}
        titleAction={(
          <Button
            title={t('dataSetsManagement.downloadExample')}
            startIcon={<DownloadIcon />}
            variant={BUTTON_VARIANT.ghost}
            className={styles.downloadButton}
            onClick={handleDownloadExample}
            isLoading={isDownloadExampleLoading}
            disabled={isBusy || sessionId == null}
          />
        )}
        showLoader={isBusy}
        isButtonsVisible={false}
      >
        <div className={styles.content}>
          <div
            className={dropZoneClassName}
            onDragEnter={rootProps.onDragEnter}
            onDragOver={rootProps.onDragOver}
            onDragLeave={rootProps.onDragLeave}
            onDrop={rootProps.onDrop}
            ref={rootProps.ref}
            tabIndex={rootProps.tabIndex}
            role="button"
            aria-label={t('valueChainMap.bomUnitsUploadDropzoneTitle')}
          >
            <input {...getInputProps()} />
            <div className={styles.dropZoneInner}>
              <div className={styles.dropZoneIcon}>
                <UploadIcon />
              </div>
              <div className={styles.dropZoneTitle}>
                {t('valueChainMap.bomUnitsUploadDropzoneTitle')}
              </div>
              <div className={styles.dropZoneSubtitle}>
                {t('valueChainMap.bomUnitsUploadDropzoneSubtitle')}
              </div>
              <Button
                title={t('common.browse')}
                variant={BUTTON_VARIANT.primary}
                className={styles.browseButton}
                onClick={openFilePicker}
                disabled={isUploadDisabled}
              />
              {dataFile ? (
                <div className={styles.fileName}>
                  {dataFile.name}
                </div>
              ) : null}
            </div>
          </div>
          {dropzoneError ? <div className={styles.error}>{dropzoneError}</div> : null}
          <div className={styles.footer}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={isBusy}
            />
          </div>
        </div>
      </Modal>
      <MappingModal<TImportRecordsMutation>
        isMappingModalVisible={isMappingModalVisible}
        onCloseMappingModal={handleCloseMappingModal}
        setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
        importRecords={handleSupplierImportRecords}
        isLoading={isBusy}
        onImportError={handleImportError}
        onImportSuccess={handleImportSuccess}
      />
      <ImportErrorsModal<TImportRecordsMutation>
        isImportErrorsModalVisible={isImportErrorsModalVisible}
        setIsImportErrorsModalVisible={handleCloseImportErrorsModal}
        importRecords={handleSupplierImportRecords}
        isLoading={isBusy}
        onImportError={handleImportError}
        disableDuplicateActions
      />
      <QuestionErrorModal
        isOpen={isImportErrorModalVisible}
        onClose={handleCloseImportErrorModal}
        title={t('valueChainMap.supplierUnitsModalTitle')}
        errorLog={importErrorMessage}
        hideRecommendations
      />
    </>
  );
}

export default AICreatorSupplierUploadModal;
